// D-059 — the capabilities §11 grants to specific built-ins, built from the kernel.
//
// This module is the whole list of who gets more than `HangarAPI`. It lives in `host/` because
// the kernel must not know about apps (§6.5) and apps must not know about the kernel.
import type {
  ArchiveShellAccess,
  ExplorerShellAccess,
  InspectorFrame,
  InspectorInstance,
  InspectorShellAccess,
  NotepadShellAccess,
  NotesShellAccess,
  CalendarNotice,
  AlarmShellAccess,
  ArcadeShellAccess,
  ChatShellAccess,
  RemindersShellAccess,
  SocialShellAccess,
  ClockShellAccess,
  ConsoleShellAccess,
  ConsoleSource,
  ManifestProblem,
  /** §34.6 (M60) — Media. */
  MediaShellAccess,
  PdfShellAccess,
  PlayShellAccess,
  RecycleBinShellAccess,
  PhotosShellAccess,
  StoreShellAccess,
  TerminalShellAccess,
  SnipShellAccess,
  VoiceShellAccess,
  WeatherPlace,
  WeatherReport,
  WeatherShellAccess,
} from '@hangar/shared';
import {
  HangarApiError,
  captureRect,
  joinVfs,
  MULTIPLAYER_GAME_IDS,
  appCatalog,
  basename,
  mimeForName,
  manifestUrlFor,
  parseManifest,
  extname,
  parentOf,
  userRoot,
  utcDayKey,
  wholeFrame,
  type VfsPath,
} from '@hangar/shared';
import type { InstallResult } from '@/kernel/apps/registry';
import type { HangarKernel } from '@/kernel';
import { confine, confineOrThrow, fileManagerCaller, type VfsCaller } from '@/kernel/vfs/confine';
import { wallpaperFileValue } from '@/assets/wallpaper';
// §34.6 (M60) — the media file-type table, shared with Peek and with Media itself (D-508/D-524).
import { kindOfExtension } from '@/assets/media/kinds';
import { importTransfer, type TransferLike } from '@/kernel/vfs/import';
import {
  DRAG_OUT_MAX_BYTES,
  SNIP_DIR,
  VOICE_MEMO_DIR,
  WB_GEOLOCATION_TIMEOUT_MS,
} from '@/config';
import { pickSnipRegion } from './snip-overlay';
import { saveBlob } from './download';
import { COPY } from '@/copy';

/** §11.1 / §7.8 — the Recycle Bin operations, plus the one thing §7.1 knows and `env()` does not. */
export function createExplorerAccess(kernel: HangarKernel): ExplorerShellAccess {
  /** The blob URL a pending drag-out is staged on. One at a time, revoked before the next. */
  let staged: string | null = null;

  /** Confine or throw, so no path reaches the tree without passing §7.1's single function. */
  const confined = (caller: VfsCaller, path: VfsPath): VfsPath => {
    const allowed = confine(caller, path, 'write');
    if (!allowed.ok) throw new HangarApiError('FORBIDDEN', `${allowed.error} for ${path}`);
    return allowed.path;
  };

  return {
    home: () => userRoot(kernel.vfs.currentUser()),

    openWithCandidates(path) {
      const registered = new Map(kernel.launch.launchable().map((app) => [app.id, app.name]));
      return kernel.filetypes.candidatesFor(path).map((match, index) => ({
        appId: match.appId,
        name: registered.get(match.appId) ?? match.appId,
        role: match.role,
        isDefault: index === 0,
      }));
    },

    capabilities: () => {
      const probed = kernel.capabilities;
      return {
        dragOut: probed.dragOut,
        nativeOpenPicker: probed.nativeOpenPicker,
        nativeSavePicker: probed.nativeSavePicker,
        directoryImport: probed.directoryImport,
      };
    },

    // §34.6 / D-496 (M60.5) — read on every call, so changing the §14 setting re-badges an open
    // window rather than needing it reopened.
    syncThresholdMb: () => kernel.settings.get('media.syncThresholdMb'),

    async importDrop(transfer, dir) {
      const caller = fileManagerCaller('explorer', kernel.vfs.currentUser());
      const into = confine(caller, dir, 'write');
      if (!into.ok) throw new HangarApiError('FORBIDDEN', `${into.error} for ${dir}`);
      const target = into.path;
      const result = await importTransfer(transfer as TransferLike, target, {
        // Every write goes back through the same confinement, so a `../` in a dropped file's
        // name cannot walk out of the folder it was dropped on (§7.1 rejects it outright).
        write: (path, bytes) => kernel.vfs.write(confined(caller, path), bytes),
        mkdir: (path) => kernel.vfs.mkdir(confined(caller, path)),
        taken: async (dir_) => (await kernel.vfs.list(confined(caller, dir_))).map((e) => e.name),
      });
      return { files: result.files, failed: result.failed };
    },

    /**
     * §10.2 — hand the drag to the shell. The payload names the *paths*, not their bytes: a
     * receiver that may read them can, and one that may not is not given a way round §9.5 by a
     * drag (D-136).
     */
    beginContentDrag(paths) {
      if (paths.length === 0) return;
      // The drag started with a pointer-down, and that focused this window (§5.3) — so the
      // focused window *is* the source, and Explorer does not need to know its own id.
      const focusedId = kernel.wm.getState().focusedId;
      const win = focusedId === null ? undefined : kernel.wm.get(focusedId);
      const items = paths.map((path) => ({ mime: mimeForName(basename(path)), path }));
      const label =
        paths.length === 1 ? basename(paths[0] as VfsPath) : `${paths.length} items`;
      kernel.drag.begin(
        { items },
        label,
        win ? { winId: win.id, rect: win.rect } : undefined,
      );
    },

    async download(path) {
      const caller = fileManagerCaller('explorer', kernel.vfs.currentUser());
      const bytes = await kernel.vfs.read(confined(caller, path));
      saveBlob(new Blob([bytes as BlobPart]), basename(path));
    },

    async prepareDragOut(path, size) {
      // §7.7 — Chromium's `DownloadURL` is `<mime>:<filename>:<url>`, and the URL has to exist
      // before `dragstart` runs, because that handler cannot await. Everywhere else the drag
      // would be inert, so the caller offers Download instead (D-042).
      if (staged) {
        URL.revokeObjectURL(staged);
        staged = null;
      }
      if (!kernel.capabilities.dragOut || size > DRAG_OUT_MAX_BYTES) return null;
      const caller = fileManagerCaller('explorer', kernel.vfs.currentUser());
      const bytes = await kernel.vfs.read(confined(caller, path));
      staged = URL.createObjectURL(new Blob([bytes as BlobPart]));
      return `application/octet-stream:${basename(path)}:${staged}`;
    },

    async readVersion(path, versionId) {
      // A capability is not an exemption: this runs the path through the *same* `confine`
      // (§7.1) that `HangarAPI.fs` would, as the file manager (§9.5).
      const caller = fileManagerCaller('explorer', kernel.vfs.currentUser());
      const allowed = confine(caller, path, 'read');
      if (!allowed.ok) throw new HangarApiError('FORBIDDEN', `${allowed.error} for ${path}`);
      return kernel.versions.read(allowed.path, versionId);
    },

    trash: {
      async list() {
        return (await kernel.trash.list()).map((entry) => ({
          key: entry.key,
          name: entry.name,
          originalPath: entry.originalPath,
          isDir: entry.isDir,
          size: entry.size,
          deletedAt: entry.deletedAt,
        }));
      },
      restore: (key) => kernel.trash.restore(key),
      deleteForever: (key) => kernel.trash.deleteForever(key),
      empty: () => kernel.trash.empty(),
    },

    // ── §34.13 (M66.3) — Send to connection ────────────────────────────────────────────────

    // D-024 — a guest has no server, no roster and no account to send from, so the verb does not
    // exist for them. Absent rather than shown and refused, the way §11.5 keeps a gated app out of a
    // guest's Store rather than locking it.
    canSendToConnection: () => kernel.identity().kind === 'user',

    async sendToConnection(path, size) {
      // The same `confine` any read of this path would pass (§9.5): the offer names a file, and a
      // capability is not an exemption from the grammar. The server checks ownership again from its
      // own `files` row (§19.3), so this is the client-side half of two independent answers.
      const caller = fileManagerCaller('explorer', kernel.vfs.currentUser());
      const allowed = confine(caller, path, 'read');
      if (!allowed.ok) throw new HangarApiError('FORBIDDEN', `${allowed.error} for ${path}`);
      return kernel.sendFileToConnection(allowed.path, size);
    },
  };
}

/**
 * §11.11 / D-059 — the Recycle Bin's capability.
 *
 * Deliberately the same four calls Explorer's `trash` sub-object makes, onto the same service:
 * §7.8 has one Trash, and the confirms that guard `deleteForever` and `empty` are inside it, so
 * neither app can be the one that forgot to ask.
 */
export function createRecycleBinAccess(kernel: HangarKernel): RecycleBinShellAccess {
  return {
    async list() {
      return (await kernel.trash.list()).map((entry) => ({
        key: entry.key,
        name: entry.name,
        originalPath: entry.originalPath,
        isDir: entry.isDir,
        size: entry.size,
        deletedAt: entry.deletedAt,
      }));
    },
    restore: (key) => kernel.trash.restore(key),
    deleteForever: (key) => kernel.trash.deleteForever(key),
    empty: () => kernel.trash.empty(),
    usage: () => kernel.trash.usage(),
    subscribe: (listener) => kernel.bus.on('fs:changed', listener),
  };
}

/**
 * §11.10 / D-059 — Clock & Calendar's capability: read §13's Center, and follow it.
 *
 * The Center is the shell's, and no app can read it through `HangarAPI` — an app that could would
 * be reading every other app's notifications. This one is the surface §11.10 says they land on.
 */
/**
 * §25.4 / D-059 — Cascade's capability: two §14 numbers and nothing else.
 *
 * Read live from the store on every call rather than snapshotted, which is what makes retuning
 * DAS in Settings take effect on the piece already falling.
 */
/**
 * §26.3 / §26.4 (D-059) — what the Connections app is handed.
 *
 * `HangarAPI` is closed and carries nothing social, deliberately: an *external* app must never be
 * able to read who somebody knows. This capability is built at composition and passed as a prop,
 * so it never travels through `mount` and an installed app has no way to obtain one.
 *
 * `confirm` comes from `shellui` rather than from the app, because §19.4 puts destructive
 * confirmations on the shell's own layer — an app that drew its own could not be told apart from
 * one that skipped it.
 */
export function createSocialAccess(kernel: HangarKernel): SocialShellAccess {
  return {
    connections: () => kernel.social.connections(),
    presence: () => kernel.social.presence(),
    invite: () => kernel.social.invite(),
    accept: (token) => kernel.social.accept(token),
    remove: (peer) => kernel.social.remove(peer),
    block: (peer) => kernel.social.block(peer),
    heartbeat: (status, gameId) => kernel.social.heartbeat(status, gameId),
    subscribe: (listener) => kernel.social.subscribe(listener),
    confirm: (message) => kernel.shellui.confirm(message, 'connections'),
    me: () => kernel.socialRef(),
    // §26.4 / §26.7 — the app asks; the shell creates the session, opens the window and rings the
    // peer. Connections can do none of those three, which is the point of it being one call.
    suggest: (gameId, peer) => kernel.startPlay(gameId, peer),
    playableGames: () => MULTIPLAYER_GAME_IDS,
    // §30.6 — the board, read through the same service the roster comes from. An empty list on a
    // failure rather than a throw: Standings is a panel, and a panel must not take the app down.
    dailyScores: (gameId, day) => kernel.social.dailyScores(gameId, day).catch(() => []),
    today: () => utcDayKey(Date.now()),
    headToHead: (peer) => kernel.social.headToHead(peer).catch(() => []),
    /**
     * §26.8 (D-586) — the live sessions, narrowed to what a roster row needs.
     *
     * The narrowing is the capability: `kernel.livePlay()` answers `PlaySession`s carrying the seed
     * and the move log, and Connections is not in these games. It gets the peer, the game, whose
     * move it is and when it started — which is a description of a row and nothing more.
     */
    async liveGames() {
      const me = kernel.socialRef();
      if (me === null) return [];
      const sessions = await kernel.livePlay();
      return sessions.map((session) => {
        const host = session.host === me;
        return {
          sessionId: session.id,
          gameId: session.gameId,
          peer: host ? session.guest : session.host,
          peerName: host ? session.guestName : session.hostName,
          state: session.state === 'invited' ? ('invited' as const) : ('active' as const),
          // `turn` is null for co-op and for an unanswered invitation, and both of those are
          // honestly "no turn" rather than "not yours" — so the row says nothing instead.
          myTurn: session.turn === null ? null : session.turn === me,
          startedAt: session.activeAt ?? session.createdAt,
        };
      });
    },
    resumeGame: (sessionId, gameId) => kernel.resumePlay(sessionId, gameId),
    endGame: (sessionId) => kernel.play.cancel(sessionId),
  };
}

/**
 * §26.7 (D-059) — what Conquest and Intercept are handed.
 *
 * Deliberately **not** `SocialShellAccess`: a game must not be able to read the roster, mint an
 * invite link or see who is online. It gets one session at a time, the four calls that session
 * needs, and the wall clock — which is here rather than reached for, because §24.4 bans the
 * substrate from reading it and `boundaries.test.ts` pins the exemption to a single file.
 */
export function createPlayAccess(kernel: HangarKernel): PlayShellAccess {
  return {
    pending: () => kernel.pendingPlay(),
    onOpen: (listener) => kernel.onOpenPlay(listener),
    // §26.8 (D-586) — one session or nothing, for the game id the caller already is. The shell
    // does the filtering, so a title never sees a list and never sees anybody else's game.
    async resume(gameId) {
      const session = await kernel.activePlay(gameId);
      return session === null ? null : { sessionId: session.id, gameId: session.gameId };
    },
    read: (sessionId, since) => kernel.play.read(sessionId, since),
    move: (sessionId, seq, payload) => kernel.play.move(sessionId, seq, payload),
    cancel: (sessionId) => kernel.play.cancel(sessionId),
    // §30.5 — the finishing client claims the verdict. A refusal is the peer having got there
    // first, which is a fact rather than a failure.
    postResult: (sessionId, result) => kernel.play.postResult(sessionId, result).catch(() => false),
    // §30.6 — the same shell path Suggest-a-game uses, pre-addressed. The game names nobody.
    async rematch(sessionId) {
      const session = await kernel.play.read(sessionId);
      const me = kernel.socialRef();
      const peer = session.host === me ? session.guest : session.host;
      await kernel.startPlay(session.gameId, peer);
    },
    subscribe: (listener) => kernel.social.subscribe(listener),
    me: () => kernel.socialRef(),
    // §26.8 F11 — one bit about one person, derived from §26.2's own 300-second rule. The game
    // never sees the status, the game id, or anybody else's row.
    async peerHere(peer) {
      const rows = await kernel.social.presence();
      return rows.some((row) => row.peer === peer && row.status !== 'offline');
    },
    now: () => Date.now(),
  };
}

/**
 * §26.5 (D-059) — what the Chat app is handed.
 *
 * `subscribe` is `social`'s doorbell rather than a chat-specific one, which is the same choice the
 * server made in `app.ts`: §26.6 gives a user **one** stream carrying every kind of frame, so a
 * second subscription here would be a second thing to unsubscribe and would still be the same
 * events. The app re-`GET`s on any frame; that is one request and no vocabulary to keep in step.
 */
export function createChatAccess(kernel: HangarKernel): ChatShellAccess {
  return {
    threads: () => kernel.chat.threads(),
    messages: (peer, options) => kernel.chat.messages(peer, options),
    send: (peer, body) => kernel.chat.send(peer, body),
    remove: (id) => kernel.chat.remove(id),
    subscribe: (listener) => kernel.social.subscribe(listener),
    confirm: (message) => kernel.shellui.confirm(message, 'chat'),
    me: () => kernel.socialRef(),
    pendingThread: () => kernel.pendingChatThread(),
    onOpenThread: (listener) => kernel.onOpenChatThread(listener),
  };
}

/**
 * §11.14 (D-059) — Alarms & Stopwatch's capability: the shell's schedule, and nothing else.
 *
 * Straight pass-through, and that is the point of it existing: the service is a kernel service and
 * §6.5 keeps `apps/**` away from those, so this is the one file that knows both names. The app
 * cannot reach the notification the shell posts, the file the schedule lives in, or anybody
 * else's alarms — it can ask for the twelve things below.
 */
export function createAlarmAccess(kernel: HangarKernel): AlarmShellAccess {
  return {
    snapshot: () => kernel.alarms.snapshot(),
    subscribe: (listener) => kernel.alarms.subscribe(listener),
    add: (fields) => void kernel.alarms.add(fields),
    remove: (id) => kernel.alarms.remove(id),
    toggle: (id, enabled) => kernel.alarms.toggle(id, enabled),
    snooze: (id) => kernel.alarms.snooze(id),
    dismiss: (id) => kernel.alarms.dismiss(id),
    startStopwatch: () => kernel.alarms.startStopwatch(),
    stopStopwatch: () => kernel.alarms.stopStopwatch(),
    lapStopwatch: () => kernel.alarms.lapStopwatch(),
    resetStopwatch: () => kernel.alarms.resetStopwatch(),
    confirm: (message) => kernel.shellui.confirm(message, 'alarms'),
  };
}

/**
 * §27 (D-059) — Reminders' capability.
 *
 * Straight pass-through onto two kernel services, and the *composition* is the point: the app is
 * handed the reminder calls and the **roster**, and nothing else. It cannot reach presence, chat,
 * the invite mint, or anybody's files. Granting it `SocialShellAccess` to get `connections` would
 * have handed it all four.
 */
export function createRemindersAccess(kernel: HangarKernel): RemindersShellAccess {
  const service = kernel.reminders;
  return {
    lists: () => service.lists(),
    createList: (name, hue) => service.createList(name, hue),
    renameList: (id, name) => service.renameList(id, name),
    recolourList: (id, hue) => service.recolourList(id, hue),
    deleteList: (id) => service.deleteList(id),

    items: (listId) => service.items(listId),
    addItem: (listId, fields) => service.addItem(listId, fields),
    updateItem: (id, patch) => service.updateItem(id, patch),
    deleteItem: (id) => service.deleteItem(id),

    shares: (listId) => service.shares(listId),
    share: (listId, kind, id, canEdit) => service.share(listId, kind, id, canEdit),
    unshare: (listId, kind, id) => service.unshare(listId, kind, id),

    groups: () => service.groups(),
    createGroup: (name) => service.createGroup(name),
    deleteGroup: (id) => service.deleteGroup(id),
    addMember: (groupId, ref) => service.addMember(groupId, ref),
    removeMember: (groupId, ref) => service.removeMember(groupId, ref),

    connections: () => kernel.social.connections(),
    subscribe: (listener) => kernel.social.subscribe(listener),
    confirm: (message) => kernel.shellui.confirm(message, 'reminders'),
    me: () => kernel.socialRef(),
  };
}

export function createArcadeAccess(kernel: HangarKernel): ArcadeShellAccess {
  return {
    dasMs: () => kernel.settings.get('arcade.dasMs'),
    arrMs: () => kernel.settings.get('arcade.arrMs'),
    // §30.4 (D-380) — the one clock read a card title is allowed, and it happens out here.
    today: () => utcDayKey(Date.now()),
    /**
     * §30.4 — the game hands over a finished daily and the shell posts it.
     *
     * Swallowed on failure, and deliberately: a daily board is a nice-to-have, and a game that
     * threw at the end of a won deal because the network blinked would be a worse game than one
     * with no board at all. The `false` is honest — nothing was stored.
     */
    postDailyScore: (entry) => kernel.social.postDailyScore(entry).catch(() => false),
  };
}

/**
 * §34.6 (D-059, D-520) — Media's mute, and nothing else.
 *
 * `subscribe` on the settings store rather than a bus event: §14 has no `settings:changed` frame,
 * and the store is the authority the Settings page itself writes through — so a mute toggled from
 * anywhere reaches a playing window. The listener is filtered to *changes* of the one key, because
 * every unrelated setting write publishes the whole values object and an unfiltered listener would
 * re-run the element's volume assignment on each of them.
 */
export function createMediaAccess(kernel: HangarKernel): MediaShellAccess {
  return {
    muted: () => kernel.settings.get('sound.muted'),

    /**
     * §34.6 (D-526) — list the opened file's folder, and answer only with media.
     *
     * `parentOf(opened)` is computed **here**, so nothing the app passes can name a different
     * directory: the worst a caller can do is ask about a folder it already holds a file in. The
     * kind filter is what keeps this from being `fs.list` with extra steps — the answer is "which
     * media files sit beside this one", which is exactly what a queue needs and no more.
     */
    async siblings(opened) {
      const caller = fileManagerCaller('media', kernel.vfs.currentUser());
      const dir = confine(caller, parentOf(opened), 'read');
      if (!dir.ok) return [];
      try {
        const entries = await kernel.vfs.list(dir.path);
        return entries.filter(
          (entry) => !entry.isDir && kindOfExtension(extname(entry.path)) !== null,
        );
      } catch {
        // A folder that cannot be listed has no siblings, which is an answer rather than a failure:
        // the file itself is playable and a one-item queue is correct for it.
        return [];
      }
    },

    /**
     * §34.6 (D-526) — bytes for the element, refused for anything that is not media.
     *
     * The type check comes **first** and is the whole point of the grant's shape: a capability that
     * confined without it would be a general reader over user space, which is far more than §34.6
     * needs. With it, the worst this can do is read a file the user could have double-clicked into
     * this same app.
     */
    async read(path) {
      if (kindOfExtension(extname(path)) === null) {
        throw new HangarApiError('FORBIDDEN', 'Media reads only the file types it registers');
      }
      const caller = fileManagerCaller('media', kernel.vfs.currentUser());
      return kernel.vfs.read(confineOrThrow(caller, path, 'read'));
    },

    onMuteChange(listener) {
      let last = kernel.settings.get('sound.muted');
      return kernel.settings.store.subscribe((values) => {
        const next = values['sound.muted'];
        if (next === last) return;
        last = next;
        listener(next);
      });
    },
  };
}

export function createClockAccess(kernel: HangarKernel): ClockShellAccess {
  const read = (): CalendarNotice[] =>
    kernel.notify.center().map((item) => ({
      id: item.id,
      appId: item.appId,
      title: item.title,
      body: item.body ?? null,
      at: item.at,
    }));

  return {
    notifications: read,

    // ── §34.11 (M64.4) — the calendar half. Three calls, and none is "schedule a reminder": the shell
    // derives every instant from the events file (`kernel/calendar/`), so the app's job is to save it.
    calendarChanged: () => kernel.calendarChanged(),
    pendingDay: () => kernel.pendingCalendarDay(),
    onOpenDay: (listener) => kernel.onOpenCalendarDay(listener),

    subscribe(listener) {
      // Three events, because §13 has three ways the Center changes and the calendar must not
      // keep showing a notification that was cleared.
      const off = [
        kernel.bus.on('notify:posted', listener),
        kernel.bus.on('notify:cleared', listener),
        kernel.bus.on('notify:dismissed', listener),
      ];
      return () => {
        for (const unsubscribe of off) unsubscribe();
      };
    },
  };
}

/**
 * §11.13 / D-191 — the Console's sources, and its export.
 *
 * Four today, all the shell's own: the kernel's diagnostic ring split by the source each entry
 * declares, plus the two logs that already existed elsewhere and had no single place to be read
 * together — §9.3's bridge frames and §13's Notification Center.
 *
 * They are assembled here, not in the kernel, for the reason this whole module exists: the
 * Console is an app, apps may not reach the kernel (§6.5), and reading every app's diagnostics
 * is precisely the sort of reach that has to be granted rather than assumed.
 */
export function createConsoleAccess(kernel: HangarKernel): ConsoleShellAccess {
  /**
   * The kernel's ring, presented as one source per `source` field.
   *
   * A filter over a shared ring rather than five rings: a log's ordering is the whole point of
   * it, and entries that arrived interleaved must stay interleaved when the user turns a source
   * back on. `seq` is global to the ring, so it orders correctly across these too.
   */
  const shellSource = (id: string, label: string): ConsoleSource => ({
    id,
    label,
    entries: () => kernel.logs.entries().filter((entry) => entry.source === id),
    subscribe: (onChange) => kernel.logs.subscribe(onChange),
    // Every one of these clears the same ring — which is right: "Clear" in a log viewer means
    // the view in front of you, and offering to empty one source and not another would be a
    // distinction the ring cannot actually make.
    clear: () => kernel.logs.clear(),
  });

  const sources: ConsoleSource[] = [
    shellSource('shell', 'Shell'),
    shellSource('apps', 'Apps'),
    shellSource('files', 'Files'),
    shellSource('bridge', 'Bridge'),
    shellSource('notify', 'Notifications'),

    // §9.3's frame log. Not mirrored into the ring (D-191): a busy app is thousands of frames a
    // minute and would bury everything else, so it stays its own source, off by default in the
    // sense that a user has to look at it.
    {
      id: 'frames',
      label: 'Bridge frames',
      entries: () =>
        kernel.bridge.log().map((frame) => ({
          seq: frame.at,
          at: frame.at,
          source: 'frames',
          level: 'debug' as const,
          appId: frame.appId,
          message: `${frame.direction === 'in' ? '←' : '→'} ${frame.key}`,
          detail: `${frame.id} · ${frame.size} B`,
        })),
      subscribe: (onChange) => {
        const off = [
          kernel.bus.on('bridge:message', onChange),
          kernel.bus.on('bridge:drop', onChange),
        ];
        return () => {
          for (const unsubscribe of off) unsubscribe();
        };
      },
    },

    // §13's Center, read-only. `clear` is deliberately absent: emptying the Notification Center
    // is a §13 gesture with its own meaning (D-139), and a log viewer must not be a second way
    // to perform it by accident.
    {
      id: 'notifications',
      label: 'Center',
      entries: () =>
        [...kernel.notify.center()]
          .reverse()
          .map((item) => ({
            seq: item.at,
            at: item.at,
            source: 'notifications',
            level: 'info' as const,
            appId: item.appId,
            message: item.title,
            ...(item.body === undefined ? {} : { detail: item.body }),
          })),
      subscribe: (onChange) => kernel.notify.subscribe(onChange),
    },
  ];

  return {
    sources: () => sources,

    async export(text) {
      // Under the user's home, not `/System`: it is the user's evidence, it should appear in
      // Explorer, and §7 gives it version history like anything else they own.
      const stamp = new Date()
        .toISOString()
        .replace(/[:.]/g, '-')
        .replace('T', '_')
        .slice(0, 19);
      const path = `${userRoot(kernel.vfs.currentUser())}/Documents/console-${stamp}.log`;
      await kernel.vfs.write(path, text);
      return path;
    },
  };
}

// §11.6's capability moved to `settings-access.ts` at M13.7: it grew seven more pages, and
// leaving it here would have buried the six small capabilities this file exists to hold.
export { createSettingsAccess } from './settings-access';

/**
 * §11.12 — the App Inspector's capability.
 *
 * Everything here is something the kernel already knows and **no app may read for itself**:
 * another app's manifest, the bridge's own message log, what a *different* app was granted. That
 * is precisely why it is a capability rather than an addition to `HangarAPI` — putting a "show me
 * every app's permissions" method on the app surface would undo §9.4.
 */
export function createInspectorAccess(kernel: HangarKernel): InspectorShellAccess {
  const instancesOf = (appId: string): InspectorInstance[] =>
    kernel.wm
      .list()
      .filter((win) => win.appId === appId)
      .map((win) => {
        const connection = kernel.bridge.get(win.instanceKey);
        return {
          instanceKey: win.instanceKey,
          winId: win.id,
          bridge: connection?.state ?? 'none',
          drops: connection?.drops ?? 0,
          grants: kernel.vfsGateway.grantsFor(win.instanceKey),
        };
      });

  return {
    apps() {
      return kernel.installed.list().map((app) => {
        // Re-validated *now*, against the origin it was served from, rather than trusted because
        // it validated at install time: a cached manifest outlives the rules it was checked under.
        const checked = parseManifest(app.manifest, manifestUrlFor(app.manifest.origin));
        const granted = new Set(app.permissions);
        return {
          id: app.id,
          name: app.manifest.name,
          version: app.manifest.version,
          origin: app.manifest.origin,
          entry: app.manifest.entry,
          installedAt: app.installedAt,
          health: app.health,
          failures: app.failures,
          granted: [...granted],
          ungranted: (app.manifest.permissions ?? []).filter((asked) => !granted.has(asked)),
          problems: checked.ok ? [] : checked.problems,
          manifest: app.manifest,
          instances: instancesOf(app.id),
        };
      });
    },

    frames(limit = 200) {
      const sent: InspectorFrame[] = kernel.bridge.log().map((entry) => {
        const slash = entry.key.indexOf('/');
        return {
          at: entry.at,
          instanceKey: entry.instanceKey,
          appId: entry.appId,
          direction: entry.direction,
          channel: entry.key.slice(0, slash),
          type: entry.key.slice(slash + 1),
          size: entry.size,
        };
      });
      // A dropped frame is a line in the same log, not a separate list — "dropped with reason"
      // only means anything next to the frames that were not (§11.12).
      const refused: InspectorFrame[] = kernel.bridge.drops().map((drop) => ({
        at: drop.at,
        instanceKey: drop.instanceKey,
        appId: drop.appId,
        direction: 'in',
        channel: '—',
        type: '—',
        size: 0,
        dropped: { reason: drop.reason, detail: drop.detail },
      }));
      return [...sent, ...refused].sort((a, b) => a.at - b.at).slice(-limit);
    },

    subscribe(onChange) {
      const off = [
        kernel.bus.on('bridge:message', onChange),
        kernel.bus.on('bridge:drop', onChange),
        kernel.bus.on('bridge:unrouted', onChange),
        kernel.bus.on('app:registryChanged', onChange),
      ];
      return () => {
        for (const unsubscribe of off) unsubscribe();
      };
    },

    /**
     * §34.3 (M57.4) — the Performance tab's rows.
     *
     * The node count comes from the **live DOM**, not from a model of it: `.win-host` is created
     * once per window and never re-keyed (§5.7), so `querySelectorAll('*')` under it is exactly the
     * subtree that window costs. An external app's frame is a cross-origin document, so it counts
     * `null` — reaching into it would throw, and reporting the iframe element's own one node would
     * be worse than saying nothing.
     */
    windows() {
      return kernel.wm.list().map((win) => {
        const external = kernel.installed.get(win.appId) !== undefined;
        const host = typeof document === 'undefined'
          ? null
          : document.querySelector<HTMLElement>(`.win-host[data-window-id="${win.id}"]`);
        return {
          winId: win.id,
          instanceKey: win.instanceKey,
          appId: win.appId,
          appName: kernel.appName(win.appId),
          title: win.title,
          nodes: external || host === null ? null : host.querySelectorAll('*').length,
          canvases: host === null ? 0 : host.querySelectorAll('canvas').length,
          external,
        };
      });
    },

    closeWindow(winId) {
      // §18 F9 — `requestClose`, not `wm.close`: a row in a diagnostic tool must not be a way around
      // an app's unsaved-changes veto.
      void kernel.requestClose(winId);
    },

    async validate(input) {
      const trimmed = input.trim();
      if (trimmed.length === 0) {
        return {
          ok: false,
          source: 'json',
          fetchedFrom: '',
          problems: [{ path: '<root>', message: 'nothing to validate' }],
        };
      }

      // JSON in hand, or a URL to go and get it from. A document that starts with `{` is the
      // former; anything else is treated as a location.
      if (trimmed.startsWith('{')) {
        let parsed: unknown;
        try {
          parsed = JSON.parse(trimmed);
        } catch (error) {
          return {
            ok: false,
            source: 'json',
            fetchedFrom: '',
            problems: [{ path: '<root>', message: `not JSON: ${(error as Error).message}` }],
          };
        }
        // §9.2's origin rule needs a URL to check against. Pasted JSON has none, so it is checked
        // against the origin it *claims* — which validates everything except the one thing only a
        // real fetch can prove, and the UI says so.
        const claimed = (parsed as { origin?: unknown }).origin;
        const from = typeof claimed === 'string' ? manifestUrlFor(claimed) : '';
        const result = parseManifest(parsed, from);
        return result.ok
          ? { ok: true, source: 'json', fetchedFrom: from, problems: [], manifest: result.manifest }
          : { ok: false, source: 'json', fetchedFrom: from, problems: result.problems };
      }

      const url = /\/\.well-known\//.test(trimmed)
        ? trimmed
        : manifestUrlFor(trimmed.replace(/\/$/, ''));
      try {
        const response = await fetch(url, { credentials: 'omit', cache: 'no-store' });
        if (!response.ok) {
          return {
            ok: false,
            source: 'url',
            fetchedFrom: url,
            problems: [{ path: '<root>', message: `${url} answered ${response.status}` }],
          };
        }
        const result = parseManifest(await response.json(), url);
        return result.ok
          ? { ok: true, source: 'url', fetchedFrom: url, problems: [], manifest: result.manifest }
          : { ok: false, source: 'url', fetchedFrom: url, problems: result.problems };
      } catch (error) {
        return {
          ok: false,
          source: 'url',
          fetchedFrom: url,
          problems: [{ path: '<root>', message: `could not fetch ${url}: ${String(error)}` }],
        };
      }
    },
  };
}

/**
 * §11.5 — the Store's capability.
 *
 * Installing is a shell act: it fetches a manifest, validates it against §9.2, and writes to the
 * registry every other surface reads. `HangarAPI` has no `install`, deliberately — §9.4's list of
 * what no app can ever do ends with "install another app".
 */
function isLocalhost(): boolean {
  if (typeof location === 'undefined') return false;
  return /^(localhost|127\.0\.0\.1|\[::1\])$/.test(location.hostname);
}

/**
 * §11.2 / D-059 — the Terminal's capability: `install <url>`, and nothing else.
 *
 * It shares the Store's rule rather than restating it — D-025 disables paste-a-URL install for
 * guests, and a second copy of that check is a second place for it to come loose. Everything else
 * §11.2's provider does goes through `HangarAPI`.
 */
export function createTerminalAccess(kernel: HangarKernel): TerminalShellAccess {
  const store = createStoreAccess(kernel);
  return {
    home: () => userRoot(kernel.vfs.currentUser()),
    canInstall: () => store.canPasteUrl(),
    installFromUrl: (url) => store.installFromUrl(url),
  };
}

/**
 * §11.7 / D-059 — Photos' one capability: **Set as wallpaper**.
 *
 * It writes the *path*, not the bytes (D-169): a data URL in `settings` would put megabytes into
 * every settings write, and the desktop can read a file perfectly well.
 */
export function createPhotosAccess(kernel: HangarKernel): PhotosShellAccess {
  return {
    setWallpaper: (path) => kernel.settings.set('wallpaper', wallpaperFileValue(path)),

    async siblings(path) {
      // The shell lists it, not the app: the app never gains reach, it gains an *answer* about a
      // folder the user has already opened something from.
      const entries = await kernel.vfs.list(parentOf(path));
      return entries.filter((entry) => !entry.isDir);
    },
  };
}

/**
 * §11.8 / D-059 — the PDF reader's one capability: **Download**.
 *
 * The same confinement Explorer's download runs through, as the *reader* rather than as a file
 * manager: §9.5 grants this window the file the user opened, and that is the only path it can
 * ever hand back here.
 */
export function createPdfAccess(kernel: HangarKernel): PdfShellAccess {
  return {
    async download(path) {
      // Confined as the reader, not exempted: §9.5's own roots plus nothing. The only path the
      // app can pass is the one it was launched with, and this is what makes that true rather
      // than merely intended.
      const caller = fileManagerCaller('pdf', kernel.vfs.currentUser());
      const bytes = await kernel.vfs.read(confineOrThrow(caller, path, 'read'));
      saveBlob(new Blob([bytes as BlobPart], { type: 'application/pdf' }), basename(path));
    },
  };
}

/**
 * §34.2 / D-059 (D-509) — Archive's one capability: **write beside the archive**.
 *
 * The problem it solves is a real hole rather than a convenience: §9.5 grants a window the file
 * it was opened on and *not* its directory, so Extract Here is refused on every sibling write
 * through `HangarAPI.fs`. Rather than widening that grant for every app, the shell does the write
 * and keeps the reach to one directory.
 *
 * Three properties make it safe, and all three are enforced here rather than trusted:
 *
 * - **The app never names a destination.** It passes the directory it was opened in and a path
 *   *relative* to it; `joinVfs` + `confineOrThrow` decide where that actually lands.
 * - **A relative path cannot climb out.** `..` and absolute segments are rejected before the join,
 *   so a zip containing `../../.ssh/authorized_keys` — the classic zip-slip — writes nothing.
 * - **It is confined as Archive.** The same `confineOrThrow` every other holder uses, so the
 *   capability is a *shortcut through one rule*, not an exemption from the gateway.
 */
export function createArchiveAccess(kernel: HangarKernel): ArchiveShellAccess {
  /** Resolve `dir` + `relative` to a real path, refusing anything that escapes `dir`. */
  function resolve(dir: VfsPath, relative: string): VfsPath {
    // Zip-slip, refused at the only place that can see both halves. A member path is data from a
    // file somebody else wrote, and it is the one input here that an attacker controls.
    const clean = relative.replace(/\\/g, '/');
    if (clean.startsWith('/') || clean.split('/').includes('..')) {
      throw new HangarApiError('FORBIDDEN', 'an archive member may not write outside the folder');
    }
    const caller = fileManagerCaller('archive', kernel.vfs.currentUser());
    const target = clean ? joinVfs(dir, clean) : dir;
    return confineOrThrow(caller, target, 'write');
  }

  return {
    async namesIn(dir, relative) {
      try {
        return (await kernel.vfs.list(resolve(dir, relative))).map((entry) => entry.name);
      } catch {
        // A directory that does not exist yet has no names in it, which is not a failure — it is
        // the answer, and it is what the caller is about to create.
        return [];
      }
    },

    async mkdir(dir, relative) {
      await kernel.vfs.mkdir(resolve(dir, relative));
    },

    async write(dir, relative, data) {
      await kernel.vfs.write(resolve(dir, relative), data);
    },
  };
}

// ── Lane C — §34.9 Voice · §34.10 Snip · §34.12 Weather (M62–M65, D-560) ───────────────────
//
// Three holders, and one thing all three have in common worth stating once: **none of them grants
// filesystem reach.** Voice and Snip each answer *which directory* they write into and nothing else;
// the writes themselves go through `api.fs` and are confined by §9.5's `extraRoots`, which names
// those two directories for those two app ids in `kernel/index.ts`. So the capability says *where*
// and the gateway decides *whether*, and widening one does not widen the other.
//
// That is deliberately **not** D-509's shape. Archive needed a capability that performs the write
// because its directory is *dynamic* — whichever folder the zip was opened in — and `extraRoots` is
// keyed by app id, so it cannot express that. §34.9 and §34.10 name **fixed** directories, so the
// narrower mechanism is available and is the one used.

/**
 * §34.9 / D-059 — Voice Memos' one call: the directory its memos live in.
 *
 * It exists because of a real gap and not a convenience: `env()` carries the user's *display name*
 * and §7.1's tree is rooted at their *id*, so no app can build a path into the user's own home.
 * §11.1's Explorer has had `home()` for the same reason since M6.
 */
export function createVoiceAccess(kernel: HangarKernel): VoiceShellAccess {
  return {
    dir: () => `${userRoot(kernel.vfs.currentUser())}/${VOICE_MEMO_DIR}` as VfsPath,
  };
}

/**
 * §34.10 / D-059 (D-494) — Snip's capability, which §34.10 calls "the whole mechanism".
 *
 * Every method is something `HangarAPI` cannot offer without offering it to **every** app, including
 * an installed third-party one:
 *
 * - `captureTab` photographs the shell's own document, external-app iframes included. An app that
 *   could call it could photograph the desktop, which is the reach D-059 exists to keep granted.
 * - `pick` dims every window and reads a rect over all of them — a surface no app can draw, because
 *   an app lives inside its `.win-host` (§5.7) and has no way out of it.
 * - `windowRect` reads the window manager's geometry, i.e. where *other* apps' windows are.
 * - `playShutter` reaches §15's sound set, which is the shell's.
 * - `dir` answers where a snip is saved — the same "where" vs "whether" split Voice uses; the write
 *   goes through `api.fs` and is confined by `extraRoots` for `snip` alone.
 *
 * The one behaviour worth reading the code for is **the track is stopped before the promise
 * resolves** (D-494). A `getDisplayMedia` track left running is a browser-level "this tab is being
 * shared" state that outlives the capture — a permanent indicator earned by one screenshot — and
 * D-494 accepted a per-capture prompt precisely so that state would never persist. Stopping it in a
 * `finally` means it is stopped even when `grabFrame` throws.
 */
export function createSnipAccess(kernel: HangarKernel): SnipShellAccess {
  return {
    async captureTab() {
      const devices = globalThis.navigator?.mediaDevices as
        | (MediaDevices & { getDisplayMedia?: (c?: DisplayMediaStreamOptions) => Promise<MediaStream> })
        | undefined;
      if (!devices?.getDisplayMedia) {
        // §34.17 — a browser that cannot capture is the same situation as a user who said no, and it
        // reaches them as the same sentence. `CANCELLED` rather than `UNSUPPORTED` for that reason.
        throw new HangarApiError('CANCELLED', 'this browser cannot capture a tab');
      }

      let stream: MediaStream;
      try {
        // §34.10's constraints exactly: this tab, offered first, so the ordinary case is one click.
        stream = await devices.getDisplayMedia({
          video: { displaySurface: 'browser' },
          preferCurrentTab: true,
        } as DisplayMediaStreamOptions);
      } catch (error) {
        // Denied, dismissed, or a picker the user closed — all one answer (§34.17).
        throw new HangarApiError('CANCELLED', `capture refused: ${String(error)}`);
      }

      const [track] = stream.getVideoTracks();
      try {
        if (!track) throw new HangarApiError('CANCELLED', 'the capture produced no video track');
        // Read through `unknown`: the DOM lib's own `ImageCapture` declaration does not include
        // `grabFrame` in this TS version, so the narrow shape this code actually uses is stated here
        // rather than depending on which lib.dom happens to be installed.
        const Capture = (globalThis as unknown as {
          ImageCapture?: new (track: MediaStreamTrack) => { grabFrame(): Promise<ImageBitmap> };
        }).ImageCapture;
        if (Capture) return await new Capture(track).grabFrame();
        // No `ImageCapture` (Safari, Firefox): one frame off a `<video>` is the same single frame by
        // a longer road, and it is a fallback rather than the path, because `grabFrame` needs no
        // element, no layout and no `play()` promise.
        return await grabViaVideoElement(stream);
      } finally {
        // D-494's clause, and it runs before the promise settles either way.
        for (const each of stream.getTracks()) each.stop();
      }
    },

    pick: (mode) =>
      pickSnipRegion(mode, {
        /**
         * Topmost first, because the picker answers "the window under the pointer" and §5.3 makes `z`
         * the stacking truth.
         *
         * Three exclusions, and the third was found by the M63.5 e2e rather than reasoned about
         * (D-564):
         *
         *   • **Minimized** — a window nobody can see is not one anybody meant to point at, and it is
         *     not in the captured frame either, so cropping to its rect would photograph whatever
         *     happens to be behind it.
         *   • **Another desktop** (§5.5) — same reason, more so.
         *   • **Snip's own windows.** Snip is necessarily focused when a capture starts, so its window
         *     is on top and is what the pointer lands on first; offered as a target, *window mode is
         *     unusable* — every click captures the tool. Excluding it means the click falls through to
         *     whatever Snip is covering, which is what the user was pointing at. The overlay already
         *     treats "no window here" as *keep waiting* rather than as a cancel, so nothing else had
         *     to change.
         */
        windows: () => {
          const activeDesktop = kernel.wm.getState().activeDesktopId;
          return kernel.wm
            .list()
            .filter(
              (win) =>
                win.appId !== 'snip' &&
                win.state !== 'minimized' &&
                win.desktopId === activeDesktop,
            )
            .sort((a, b) => b.z - a.z);
        },
        hintFor: (which) =>
          which === 'region' ? COPY.snip.hintRegion : which === 'window' ? COPY.snip.hintWindow : COPY.snip.hintFull,
      }),

    windowRect(windowId, frame) {
      const win = kernel.wm.get(windowId);
      if (!win) return null;
      return captureRect({ x: win.rect.x, y: win.rect.y, width: win.rect.w, height: win.rect.h }, frame);
    },

    cssRect: (rect, frame) => captureRect(rect, frame),

    wholeRect: (frame) => wholeFrame(frame),

    // §15's `screenshot` SoundId, declared since M0 and fired for the first time here (§34.10).
    playShutter: () => kernel.sound.play('screenshot'),

    dir: () => `${userRoot(kernel.vfs.currentUser())}/${SNIP_DIR}` as VfsPath,
  };
}

/**
 * One frame off a `<video>`, for a browser with no `ImageCapture`.
 *
 * Off-document and revoked immediately: the element is never appended, so it cannot be photographed
 * by the very capture it is decoding, and `loadeddata` rather than `canplay` is the event that
 * guarantees there is a frame to read.
 */
async function grabViaVideoElement(stream: MediaStream): Promise<ImageBitmap> {
  const video = document.createElement('video');
  video.srcObject = stream;
  video.muted = true;
  await new Promise<void>((resolve, reject) => {
    video.onloadeddata = () => resolve();
    video.onerror = () => reject(new HangarApiError('CANCELLED', 'the capture could not be decoded'));
    void video.play().catch(reject);
  });
  try {
    return await createImageBitmap(video);
  } finally {
    video.srcObject = null;
  }
}

/**
 * §34.12 / D-059 — Weather's capability: two §14 settings, one server route, one browser prompt.
 *
 * Modelled on `ArcadeShellAccess`, which reads two §14 numbers the same way and for the same reason:
 * §4.1 has no settings surface, deliberately, because an app that could read the settings store could
 * read every other app's preferences.
 *
 * `forecast` is here rather than a `fetch` in the app because §34.8's route is the shell's business.
 * The app hands over a latitude and a longitude and **never a URL**, which is the property D-491 is
 * about — and having the call live here means the app has no way to express one.
 *
 * `locate` is the browser's geolocation, and it is only ever called from §34.12's button: a capability
 * cannot enforce "never unprompted" by itself, but keeping it off `HangarAPI` means no *other* app can
 * ask at all, which is the part that matters.
 */
export function createWeatherAccess(kernel: HangarKernel): WeatherShellAccess {
  return {
    locations: () => kernel.settings.get('weather.locations'),
    setLocations: (locations) => kernel.settings.set('weather.locations', [...locations]),
    units: () => kernel.settings.get('weather.units'),
    /**
     * Both settings live in one store, so one subscription covers them and the app re-reads.
     *
     * `settings.store.subscribe` is the vanilla zustand handle §14 exposes for exactly this; the app
     * never sees the store itself, only the unsubscribe. It fires on *any* setting changing, which is
     * a few needless re-reads of two values and a great deal simpler than a per-key notifier.
     */
    subscribe: (listener) => kernel.settings.store.subscribe(listener),

    async forecast(lat, lon) {
      try {
        const response = await fetch(`/api/weather?lat=${lat}&lon=${lon}`, {
          credentials: 'same-origin',
          headers: { accept: 'application/json' },
        });
        // §34.17 — a 503 (empty cache), a 401, an offline shell and a body that is not JSON are all
        // "unreachable" to the app. One answer, because §34.12 gives them one sentence.
        if (!response.ok) return { kind: 'unreachable' };
        return { kind: 'ok', report: (await response.json()) as WeatherReport };
      } catch {
        return { kind: 'unreachable' };
      }
    },

    /**
     * §34.8 (D-588) — place search, through the shell for `forecast`'s reason.
     *
     * The app hands over what the user typed and nothing else. It cannot name a provider, cannot
     * name a host, and cannot see that there are two of them — which is the same property D-491
     * bought for the forecast, extended to the route that arrived after it.
     */
    async search(query) {
      try {
        const response = await fetch(`/api/weather/search?q=${encodeURIComponent(query)}`, {
          credentials: 'same-origin',
          headers: { accept: 'application/json' },
        });
        if (!response.ok) return { kind: 'unreachable' };
        const body = (await response.json()) as { places?: WeatherPlace[] };
        // An absent `places` is a malformed answer, not an empty search — but the two are the same
        // sentence to the person looking at the panel, so it reads as "nothing found" rather than
        // as an error the app would then have to explain.
        return { kind: 'ok', places: body.places ?? [] };
      } catch {
        return { kind: 'unreachable' };
      }
    },

    /**
     * §34.8 (D-589) — the coordinate's name. `null` on every unhappy path, including a 4xx.
     *
     * The route answers 200 with a null place for an unreachable provider, so a non-ok status here
     * means something structural (signed out, offline shell). Both end in the same place: the
     * caller falls back to the copy it already has.
     */
    async place(lat, lon) {
      try {
        const response = await fetch(`/api/weather/place?lat=${lat}&lon=${lon}`, {
          credentials: 'same-origin',
          headers: { accept: 'application/json' },
        });
        if (!response.ok) return null;
        const body = (await response.json()) as { place?: WeatherPlace | null };
        return body.place ?? null;
      } catch {
        return null;
      }
    },

    locate() {
      const geo = globalThis.navigator?.geolocation;
      if (!geo) return Promise.resolve(null);
      return new Promise((resolve) => {
        geo.getCurrentPosition(
          (position) => resolve({ lat: position.coords.latitude, lon: position.coords.longitude }),
          // A refusal is `null`, not a throw: the user saying no is an answer, and §34.12 has a
          // sentence for it.
          () => resolve(null),
          { timeout: WB_GEOLOCATION_TIMEOUT_MS },
        );
      });
    },
  };
}

export function createStoreAccess(kernel: HangarKernel): StoreShellAccess {
  /** The last attempt's problems per app, so a card can explain a refusal (D-133). */
  const problems = new Map<string, ManifestProblem[]>();

  const record = (appId: string, result: InstallResult): { ok: boolean; problems: ManifestProblem[] } => {
    if (result.ok) {
      problems.delete(appId);
      return { ok: true, problems: [] };
    }
    problems.set(appId, result.problems);
    return { ok: false, problems: result.problems };
  };

  return {
    cards() {
      // D-049's fixtures are served from `localhost`, so they are only ever installable when the
      // shell is *on* localhost — which is exactly dev and the e2e preview, and never production.
      // A build flag would say the same thing less truthfully: what gates them is where they live.
      // §24.11 (D-205) — a signed-out visitor's Store omits the gated games entirely.
      return appCatalog(isLocalhost(), { signedIn: kernel.identity().kind === 'user' }).map((entry) => {
        const installed = kernel.installed.get(entry.id);
        return {
          id: entry.id,
          name: entry.name,
          description: entry.description,
          // D-203 — a `builtin` entry has no origin. The card shows the app id's own home rather
          // than an empty field, since "where does this come from" is what the row means.
          origin: entry.origin ?? 'Built in',
          category: entry.category,
          // §29.1 (D-369, D-434) — carried through so the Store shelves a card under the same
          // sub-group header the launcher does. Spread rather than assigned: `exactOptionalPropertyTypes`
          // is on, and an explicit `undefined` is not the same as an absent field.
          ...(entry.group === undefined ? {} : { group: entry.group }),
          permissions: entry.permissions,
          // D-043 — an app that is not installed has no cached icon, so it gets the deterministic
          // generated one. The card looks the same before and after, which is the point.
          icon: kernel.appIconSvg(entry.id, 48),
          installed: installed !== undefined,
          unhealthy: installed?.health === 'unhealthy',
          problems: problems.get(entry.id) ?? [],
        };
      });
    },

    async install(appId) {
      const signedIn = kernel.identity().kind === 'user';
      const entry = appCatalog(isLocalhost(), { signedIn }).find((candidate) => candidate.id === appId);
      if (!entry) {
        return { ok: false, problems: [{ path: '<root>', message: `no catalog app '${appId}'` }] };
      }
      // D-203 — a builtin installs with **no network call at all**: there is no origin to fetch a
      // manifest from and no §9.6 health check to schedule, because the code is already here.
      // Install means pinned to launcher and bar; the chunk arrives on first launch (D-199/D-204).
      if (entry.kind === 'builtin') {
        return record(appId, await kernel.installed.installBuiltin(entry));
      }
      if (entry.origin === undefined) {
        return { ok: false, problems: [{ path: 'origin', message: `catalog app '${appId}' has no origin` }] };
      }
      return record(appId, await kernel.installed.install(entry.origin));
    },

    async uninstall(appId) {
      // §11.5 — "Uninstall removes and drops cached data after a confirm". §19.4 draws it.
      const app = kernel.installed.get(appId);
      const confirmed = await kernel.shellui.confirm(COPY.storeUninstallConfirm(app?.manifest.name ?? appId));
      if (!confirmed) return false;
      // Its windows go first: a window whose app has been uninstalled has nothing left to be.
      for (const win of kernel.wm.list()) if (win.appId === appId) kernel.wm.close(win.id);
      return kernel.installed.uninstall(appId);
    },

    // D-025 — v1 installs first-party apps only, and v1 has no signed-in user yet (M12). The
    // field is present and disabled, which is what §11.5 asks for.
    canPasteUrl: () => kernel.identity().kind !== 'guest',

    async installFromUrl(origin) {
      if (kernel.identity().kind === 'guest') {
        return { ok: false, problems: [{ path: '<root>', message: COPY.storePasteUrlGuest }] };
      }
      const trimmed = origin.trim().replace(/\/$/, '');
      return record(trimmed, await kernel.installed.install(trimmed));
    },

    subscribe(onChange) {
      return kernel.bus.on('app:registryChanged', onChange);
    },
  };
}

// ── §34.3 / §34.4 — Workbench lane B (M57.2, M58.1) ────────────────────────────────────────

/**
 * §34.3 (D-541, D-542) — Notepad's capability, for the markdown preview and nothing else.
 *
 * Notepad had none before this milestone, and it is worth being explicit about why it needs one now:
 * the preview has to read a §14 setting and it has to read the images a document points at, and §4.1
 * offers no route to either. Both are answers about something the user already opened, not reach.
 */
export function createNotepadAccess(kernel: HangarKernel): NotepadShellAccess {
  /** The one place a path written inside a document turns into a path on the filesystem. */
  function resolve(docPath: VfsPath, target: string): VfsPath {
    const clean = target.replace(/\\/g, '/');
    const caller = fileManagerCaller('notepad', kernel.vfs.currentUser());
    const wanted = clean.startsWith('/') ? clean : joinVfs(parentOf(docPath), clean);
    return confineOrThrow(caller, wanted, 'read');
  }

  return {
    mdDefaultView: () => kernel.settings.get('notepad.mdDefaultView'),

    async openLink(docPath, target) {
      // `openPath` is the shell's own open (§9.2): it picks the app from the file-type registry and
      // grants the *new* window the file. Notepad gains nothing by it — it never sees the bytes.
      kernel.openPath(resolve(docPath, target));
    },

    async readImage(docPath, target) {
      // The one input an attacker controls is `target` — it is written inside a document that may
      // have come from anywhere — so it is resolved and confined rather than trusted. A relative
      // path lands beside the document (the D-509 shape, read-only); an absolute one is still
      // confined to the user's own space, which is where every `![](/Users/…/x.png)` a person
      // actually writes already is. `/System` and another user's files are refused either way.
      return kernel.vfs.read(resolve(docPath, target));
    },
  };
}

/**
 * §34.4 — Quick Notes' one grant: **the global capture chord**.
 *
 * The chord itself is declared in all three keymaps as a `reserved` binding of the `notes.capture`
 * shell action (§6.4), which is what makes it reach the shell even with a game or an external app's
 * iframe focused — §9.7's arbitration, unchanged, with one more action in the closed set.
 *
 * What this capability adds is the *arming*: until Quick Notes registers, the action is bound and
 * fires nothing. That is deliberate. A chord wired straight into the keymap's dispatch would work
 * whether or not the app existed, and "which app owns this key" would have no answer in code.
 *
 * The re-registration on `notes.captureChord` is the other half of one grant rather than a second
 * one: a rebind has to reach the same arbitration, and doing it here keeps the app from ever
 * touching `keymap.overrides` — which is every *other* action's binding.
 */
export function createNotesAccess(kernel: HangarKernel): NotesShellAccess {
  return {
    // D-547 — the path §34.4 names, answered because §4.1 reports an identity and not a user id.
    // Recomputed per call: a guest who signs in gets a different home, and a cached string would
    // leave the app writing into the previous session's space.
    notesDir: () => `${userRoot(kernel.vfs.currentUser())}/Documents/Notes` as VfsPath,

    registerCaptureChord() {
      kernel.applyKeyOverrides();
      const off = kernel.bus.on('settings:changed', ({ key }) => {
        // A rebind is a *re-registration*: the same one override key, written again from the
        // setting. The app never learns what the chord is, which is why it cannot collide with one.
        if (key === 'notes.captureChord') kernel.applyKeyOverrides();
      });
      const disarm = kernel.armNotesCapture();
      return () => {
        off();
        disarm();
      };
    },
  };
}
